import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useConfig } from "../hooks/useConfig";

export default function ConfigPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { config, loading, updateConfig } = useConfig(restaurantId);

  const [orderDelay, setOrderDelay] = useState<string>("");
  const [deliveryDelay, setDeliveryDelay] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading) {
      setOrderDelay(config.orderDelayThreshold.toString());
      setDeliveryDelay(config.deliveryDelayThreshold.toString());
    }
  }, [config, loading]);

  const handleSave = async () => {
    setSaving(true);
    await updateConfig({
      orderDelayThreshold: Number(orderDelay),
      deliveryDelayThreshold: Number(deliveryDelay),
    });
    setSaving(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h4" mb={3} fontWeight={800} color="primary.main">
        ⚙️ Configuración del Sistema
      </Typography>

      <Card variant="outlined" sx={{ borderRadius: 3, maxWidth: 600 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={700} mb={3}>
            Límites de Tiempos y Alertas
          </Typography>

          <Stack spacing={4}>
            {/* Órdenes */}
            <Box>
              <Typography variant="subtitle2" fontWeight={800} mb={1}>
                Tiempo de preparación de Órdenes
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                ¿Cuántos minutos deben pasar para que una orden activa en cocina se marque como Demorada (Rojo)?
              </Typography>
              <TextField
                label="Límite para Órdenes"
                type="number"
                variant="outlined"
                fullWidth
                value={orderDelay}
                onChange={(e) => setOrderDelay(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutos</InputAdornment>,
                }}
              />
            </Box>

            {/* Domiciliarios */}
            <Box>
              <Typography variant="subtitle2" fontWeight={800} mb={1}>
                Tiempo en ruta de Domiciliarios
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                ¿Cuántos minutos deben pasar para que el pedido asignado a un repartidor se marque como Demorado?
              </Typography>
              <TextField
                label="Límite para Delivery"
                type="number"
                variant="outlined"
                fullWidth
                value={deliveryDelay}
                onChange={(e) => setDeliveryDelay(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">minutos</InputAdornment>,
                }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !orderDelay || !deliveryDelay}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                {saving ? "Guardando..." : "Guardar Configuración"}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
